import React, { useEffect } from 'react'
import { connect as connectRedux } from 'react-redux'
import { List, Map } from 'immutable'
import compose from 'recompose/compose'
import _isNil from 'lodash/isNil'
import _get from 'lodash/get'
import { getBoundActions } from '../../actions/index'
import { getSeo } from '../../services/url/index'
import { connect as connectStaticText } from '../StaticText/connect'
import SocialSharingButtons from '../SocialSharingButtons/index'
import Button, { TYPES } from '../Button/index'
import { getFacebookHref, getTwitterHref } from '../../services/share/index'
import { isEventCancelled } from '../EventsPage/EventList'
import { JOIN_NOW_URL, JOIN_NOW_QUERY } from '../../services/live-access-events/constants'
import { TYPE_MY_ACCOUNT_CHANGE_PLAN_CONFIRM, TYPE_MY_ACCOUNT_CHILD_PROFILE_CHANGE_PLAN } from '../../services/dialog/index'
import { PLAN_SKU_INCREASED_PRICE_GAIA_PLUS, PLAN_CATEGORY_AI_ONLY } from '../../services/plans/index'
import { URL_GAIA_PLUS, URL_MARKETPLACE_LOGGED_IN } from '../../services/url/constants'
import { hasLiveAccessEntitlement, isEntitled } from '../../services/subscription/index'
import { useGetPlanDataBySku } from '../../services/plans/hooks'
import { isPrimaryAccountProfile } from '../../services/user-account/index'
import { get as getConfig } from '../../config/index'
import Banner from './Banner'
import EventInformation from './EventInformation'
import Logistics from './Logistics'
import Schedule from './Schedule'
import PromoVideoBanner from './PromoVideoBanner'
import SpeakerInfo from './SpeakerInfo'

const config = getConfig()
const planGraphQLActive = !!_get(config, ['features', 'checkout', 'planGraphQL'])

const EVENT_LIVE_MARGIN_HOURS = 1

function componentFromHTML (html) {
  // eslint-disable-next-line react/no-danger
  return <div dangerouslySetInnerHTML={{ __html: html }} />
}

const getEventDetails = (props) => {
  const { location, language } = props
  const route = location.pathname.split('/').pop()
  props.getLiveAccessEventDetails({ language, id: route })
}

const isEventActive = ({ eventDetails }) => {
  if (!eventDetails) {
    return false
  }

  const now = Date.now()
  const startDate = Date.parse(eventDetails.get('startDate'))
  const endDate = Date.parse(eventDetails.get('endDate'))

  if (Number.isNaN(startDate) || Number.isNaN(endDate)) {
    return false
  }

  // Check if event is ongoing or in the margin before start
  const isEventOngoing = now >= startDate && now <= endDate
  const hoursUntilStart = (startDate - now) / (1000 * 60 * 60)
  const isWithinMarginBeforeStart = hoursUntilStart <= EVENT_LIVE_MARGIN_HOURS
    && hoursUntilStart >= 0

  return isWithinMarginBeforeStart || isEventOngoing
}

const BuyTicket = ({ eventDetails, staticText, isAnonymous }) => {
  if (eventDetails === undefined) { return null }

  const ticketsAvailable = eventDetails.get('ticketsAvailable')
  const ticketUrl = eventDetails.get('ticketing') || ''
  const linkUrl = isAnonymous ? ticketUrl : `${URL_MARKETPLACE_LOGGED_IN}?redirect=${encodeURIComponent(ticketUrl)}`

  return (
    <Button
      type={TYPES.PRIMARY}
      url={ticketsAvailable ? linkUrl : undefined}
      target="_blank"
      className="event-details-page__buy-tickets"
      disabled={!ticketsAvailable}
      directLink
    >
      {staticText.getIn(['data', ticketsAvailable ? 'buyTicket' : 'soldOut'])}
    </Button>
  )
}

const WatchLive = ({ buttonText, disabled = true }) => {
  return (
    <Button
      type={TYPES.PRIMARY}
      url={!disabled ? '/live' : null}
      disabled={disabled}
    >
      {buttonText}
    </Button>
  )
}

const SignUpMember = ({ staticText }) => {
  return (
    <Button
      type={TYPES.PRIMARY}
      url={JOIN_NOW_URL}
      query={JOIN_NOW_QUERY}
      target="_blank"
      className="event-details-page__upgrade-member"
      scrollToTop
    >
      {staticText.getIn(['data', 'joinNow'])}
    </Button>
  )
}

const UpgradeMember = ({ buttonText, onClick, planLoaded }) => {
  return (
  <Button
    type={TYPES.PRIMARY}
    onClick={
      planLoaded ? () => (onClick()) : null
    }
    disabled={!planLoaded}
  >
    {buttonText}
  </Button>
  )
}

/* Temporarily disabled (Buy Button: Phase 2)
const TicketingFrame = ({ data }) => {
  const [src, height] = data.split(',')
  const style = {
    height,
    width: '100%',
    border: 'none',
  }
  return (
    <div className="event-details-page__ticketing">
      <iframe
        className="event-details__ticketing-iframe"
        title="live-access-tickets"
        src={src}
        style={style}
      />
    </div>
  )
}
*/

const EventsDetailPage = (props) => {
  useEffect(() => {
    getEventDetails(props)
  }, [])

  useEffect(() => {
    const { location, eventDetails } = props
    if (eventDetails) {
      const speakerName = eventDetails.get('speakerName')
      const titleShort = eventDetails.get('titleShort')
      const title = `${eventDetails.get('speakers').size === 1 || !_isNil(eventDetails.get('keynoteSpeakerId')) ? `${speakerName} - ` : ''}${titleShort}`
      props.setPageSeo(
        {
          ...getSeo({
            pathname: location.pathname,
            loggedIn: !!props.auth.get('jwt'),
          }),
          title,
          ogTitle: title,
          location,
          noIndex: false,
          noFollow: false,
        },
      )
    }
  }, [props.eventDetails])

  useEffect(() => {
    getEventDetails(props)
  }, [props.language])

  const {
    eventDetails,
    staticText,
    location,
    language,
    query,
    isAnonymous,
    isLiveAccessMember,
    setPlanChangeSelected,
    renderModal,
    auth,
    userAccount,
  } = props
  const route = location.pathname.split('/').pop()
  const userPlanSku = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'planSku'])
  const gqlPlanData = useGetPlanDataBySku({
    userLanguage: language,
    authJwt: auth.get('jwt'),
    planSkus: PLAN_SKU_INCREASED_PRICE_GAIA_PLUS,
  }) || Map()
  const gaiaPlusPlan = gqlPlanData.get('plans', List()).first()
  const gaiaPlusPlanComplete = gqlPlanData.get('complete', false)
  const gqlUserPlanData = useGetPlanDataBySku({
    userLanguage: language,
    authJwt: auth.get('jwt'),
    planSkus: userPlanSku,
    forceRefresh: true,
  }) || Map()
  const userPlanComplete = gqlUserPlanData.get('complete', false)
  const userPlanData = gqlUserPlanData.get('plans', List()) || List()
  const currentPlan = userPlanData.get(0) || Map()
  const isAccountOwner = isPrimaryAccountProfile(props)
  const userPlanCategory = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'planCategory'])
  const isAiOnly = userPlanCategory === PLAN_CATEGORY_AI_ONLY

  if (eventDetails === undefined) { return null }
  const socialSharingLink = `https://gaia.com${URL_GAIA_PLUS}/${route}`
  const trackSchedule = eventDetails.getIn(['tracks', 0, 'schedule'])
  const hasSchedule = trackSchedule && trackSchedule.size > 0
  const speakers = eventDetails.get('speakers')
  const infoHeaders = [
    staticText.getIn(['data', 'eventOverview']),
    staticText.getIn(['data', 'eventDetails']),
    staticText.getIn(['data', 'speakerInfo']),
  ]
  const infoContents = [
    componentFromHTML(eventDetails.get('overview')),
    componentFromHTML(eventDetails.get('descriptionLong')),
  ]
  if (speakers.size > 1) {
    infoContents.splice(2, 0, <SpeakerInfo language={language} />)
  } else {
    infoContents.push(componentFromHTML(eventDetails.get('speakerBio')))
  }
  if (hasSchedule) {
    infoHeaders.splice(2, 0, staticText.getIn(['data', 'schedule']))
    infoContents.splice(2, 0, <Schedule route={route} language={language} />)
  }

  /* Temporarily disabled (Buy Button: Phase 2)
  const ticketingiFrameUrl = eventDetails.get('ticketingIframeUrl')
  if (ticketingiFrameUrl) {
    infoHeaders.push(staticText.getIn(['data', 'tickets']))
    infoContents.push(<TicketingFrame data={ticketingiFrameUrl} />)
  }
  */

  const promoUrl = eventDetails.get('promoUrl')
  const dateString = isEventCancelled(eventDetails.get('startDate'))
    ? staticText.getIn(['data', 'cancelled']) : eventDetails.get('localeTitleDate')

  const onClickAccountChangePlan = (plan, userCurrentPlan) => {
    setPlanChangeSelected(plan)
    renderModal(TYPE_MY_ACCOUNT_CHANGE_PLAN_CONFIRM, {
      hideDismiss: false,
      ...(planGraphQLActive && { currentPlan: userCurrentPlan }),
    })
  }

  const onClickChildProfileChangePlan = () => {
    renderModal(TYPE_MY_ACCOUNT_CHILD_PROFILE_CHANGE_PLAN, {
      hideDismiss: false,
    })
  }

  return (
    <div className="event-details-page">
      {promoUrl ? <PromoVideoBanner
        speaker={eventDetails.get('speakerName')}
        dateText={dateString}
        title={eventDetails.get('titleShort')}
        media={eventDetails.get('heroMedia')}
        promoUrl={promoUrl}
      /> : <Banner
        speaker={(eventDetails.get('speakers').size === 1 || !_isNil(eventDetails.get('keynoteSpeakerId'))) ? eventDetails.get('speakerName') : null }
        dateText={dateString}
        title={eventDetails.get('titleShort')}
        location={eventDetails.get('venueLocation')}
        media={eventDetails.get('heroMedia')}
      />
      }
      <div className="top-bar-wrapper">
        <div className="top-bar">
          <div className="event-details-page__button-box">
            <BuyTicket
              staticText={staticText}
              eventDetails={eventDetails}
              isAnonymous={isAnonymous}
            />
            {isAnonymous && <SignUpMember staticText={staticText} />}
            {!isAnonymous && !isLiveAccessMember && !isAiOnly
              && <UpgradeMember
                buttonText={staticText.getIn(['data', 'upgradeMember'])}
                onClick={() => {
                  if (isAccountOwner) {
                    return onClickAccountChangePlan(gaiaPlusPlan, currentPlan)
                  }

                  if (!isAccountOwner) {
                    return onClickChildProfileChangePlan()
                  }

                  return null
                }}
                planLoaded={isAccountOwner ? gaiaPlusPlanComplete && userPlanComplete : true}
              />}
            {isLiveAccessMember && (
              <WatchLive
                buttonText={staticText.getIn(['data', 'watchLive'])}
                disabled={!isEventActive({ eventDetails })}
              />
            )}
          </div>
          <div className="share-wrapper">
            <div className="share-label">
                Share:
            </div>
            <SocialSharingButtons
              facebookUrl={getFacebookHref(socialSharingLink, true)}
              twitterUrl={getTwitterHref(socialSharingLink, eventDetails.get('twitterText'), '', '', true)}
            />
          </div>
        </div>
      </div>
      <div id="event-information">
        <EventInformation
          speaker={eventDetails.get('speakerName')}
          title={eventDetails.get('title')}
          sale={`${staticText.getIn(['data', 'ticketsOnSale'])} ${eventDetails.get('ticket_sale_date')}`}
          startDate={eventDetails.get('localeStartDate')}
          endDate={eventDetails.get('localeEndDate')}
          headers={infoHeaders}
          selectedIndex={parseInt(query.get('default'), 10) || 0}
          contents={infoContents}
        />
      </div>
      <div className="online-experience-section">
        <div className="online-experience-heading">
          {
            isLiveAccessMember
              ? staticText.getIn(['data', 'cantAttendEventsPlus'])
              : staticText.getIn(['data', 'cantAttend'])
          }
        </div>
        <p />
        { !isLiveAccessMember && (
          <div>
            <Button
              type={TYPES.SECONDARY}
              url={URL_GAIA_PLUS}
              scrollToTop
            >
              {staticText.getIn(['data', 'moreInfo'])}
            </Button>
          </div>
        )}
      </div>
      <Logistics title={staticText.getIn(['data', 'planYourStay'])} staticText={staticText} />
    </div>
  )
}

export default compose(
  connectRedux(
    (state) => {
      const subscriptions = state.auth.get('subscriptions', List())
      return {
        auth: state.auth,
        app: state.app,
        eventDetails: state.liveAccessEvents.getIn(['eventDetails', state.user.getIn(['data', 'language', 0], 'en'), 'data', 'event']),
        language: state.user.getIn(['data', 'language', 0], 'en'),
        query: state.resolver.get('query'),
        isAnonymous: !state.auth.get('username'),
        isLiveAccessMember: isEntitled(subscriptions) && hasLiveAccessEntitlement(subscriptions),
        userAccount: state.userAccount,
        user: state.user,
      }
    },
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setPageSeo: actions.page.setPageSeo,
        getLiveAccessEventDetails: actions.liveAccessEvents.getLiveAccessEventDetails,
        setPlanChangeSelected: actions.plans.setPlanChangeSelected,
        renderModal: actions.dialog.renderModal,
      }
    },
  ),
  connectStaticText({ storeKey: 'eventDetailsPage' }),
)(EventsDetailPage)
